// =====================================================================
// ADMIN · FINANZAS (privado, solo admin)
//   - Gastos: registro de gastos + recurrentes mensuales (bodega, nómina, software)
//   - P&L mensual: ingresos − COGS (landed) − gastos = utilidad real
// =====================================================================

import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { theme } from '../../../styles/theme';
import { useCurrency } from '../../../lib/currency';
import { AdminContainer } from '../AdminShell';

const CATS = ['bodega', 'nomina', 'software', 'logistica', 'marketing', 'impuestos', 'comisiones', 'otros'];
const CAT_LABEL: Record<string, string> = {
  bodega: 'Bodega', nomina: 'Nómina', software: 'Software', logistica: 'Logística',
  marketing: 'Marketing', impuestos: 'Impuestos', comisiones: 'Comisiones', otros: 'Otros',
};

type Expense = {
  id: string; expense_date: string; category: string; description: string | null;
  payee: string | null; amount: number; currency: string; is_recurring: boolean; recurrence: string | null; active: boolean;
};

export function AdminFinanzas() {
  const [sub, setSub] = useState<'pnl' | 'gastos'>('pnl');
  return (
    <AdminContainer title="Finanzas" eyebrow="🔒 Gastos y P&L — privado (admin)">
      <div style={{ display: 'inline-flex', background: theme.bg, border: `1px solid ${theme.border}`, marginBottom: 20 }}>
        <SubBtn active={sub === 'pnl'} onClick={() => setSub('pnl')}>📊 P&L mensual</SubBtn>
        <SubBtn active={sub === 'gastos'} onClick={() => setSub('gastos')}>🧾 Gastos</SubBtn>
      </div>
      {sub === 'pnl' && <PnL />}
      {sub === 'gastos' && <Gastos />}
    </AdminContainer>
  );
}

// ===================== P&L =====================
function PnL() {
  const { usdMxn } = useCurrency();
  const now = new Date();
  const [ym, setYm] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true); setErr(null);
      const [y, m] = ym.split('-').map(Number);
      const { data: d, error } = await supabase.rpc('monthly_pnl', { p_year: y, p_month: m, p_usd_mxn: usdMxn || 18 });
      if (error) setErr(error.message); else setData(d);
      setLoading(false);
    })();
  }, [ym, usdMxn]);

  const mx = (n: number) => `$${Number(n).toLocaleString('es-MX', { maximumFractionDigits: 0 })}`;

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
        <label style={{ fontSize: 12, color: theme.textMuted }}>Mes:</label>
        <input type="month" value={ym} onChange={e => setYm(e.target.value)}
          style={{ padding: '8px 12px', border: `1px solid ${theme.border}`, background: theme.bg, color: theme.text, fontSize: 13, fontFamily: theme.font.sans }} />
        {usdMxn && <span style={{ fontSize: 11, color: theme.textMuted }}>USD/MXN {usdMxn.toFixed(2)}</span>}
      </div>

      {loading ? <div style={{ padding: 30, color: theme.textMuted }}>Calculando…</div>
        : err ? <div style={{ padding: 14, background: 'rgba(220,38,38,0.05)', border: `1px solid ${theme.err}`, color: theme.err, fontSize: 13 }}>{err}</div>
        : data && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
            <Card label="Ingresos" value={mx(data.ingresos_mxn)} sub={`${data.orders} pedidos`} />
            <Card label="Costo de venta (COGS)" value={`− ${mx(data.cogs_mxn)}`} sub="landed real" />
            <Card label="Utilidad bruta" value={mx(data.utilidad_bruta_mxn)} accent />
            <Card label="Gastos del mes" value={`− ${mx(data.gastos_mxn)}`} />
            <Card label="Utilidad neta" value={mx(data.utilidad_neta_mxn)} big accent={data.utilidad_neta_mxn >= 0} danger={data.utilidad_neta_mxn < 0} />
            <Card label="Margen neto" value={data.margen_neto_pct != null ? `${data.margen_neto_pct}%` : '—'} />
          </div>

          {data.items_sin_costo > 0 && (
            <div style={{ padding: 12, marginBottom: 16, background: 'rgba(245,158,11,0.08)', border: '1px solid #F59E0B', fontSize: 12, color: '#92400E' }}>
              ⚠️ {data.items_sin_costo} línea(s) vendida(s) sin costo capturado — el COGS está subestimado. Captura esos costos en Compras → Costos para un P&L exacto.
              ({data.items_con_costo} con costo)
            </div>
          )}

          <h3 style={{ fontFamily: theme.font.head, fontSize: 13, fontWeight: 700, color: theme.brand, textTransform: 'uppercase', letterSpacing: 0.6, margin: '0 0 10px' }}>Gastos por categoría</h3>
          <div style={{ background: theme.bg, border: `1px solid ${theme.border}` }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <tbody>
                {(data.gastos_por_categoria ?? []).map((g: any) => (
                  <tr key={g.category} style={{ borderBottom: `1px solid ${theme.border}` }}>
                    <td style={{ padding: '8px 12px' }}>{CAT_LABEL[g.category] ?? g.category}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>{mx(g.amount)}</td>
                  </tr>
                ))}
                {(!data.gastos_por_categoria || data.gastos_por_categoria.length === 0) && (
                  <tr><td style={{ padding: 14, color: theme.textMuted, fontSize: 12 }}>Sin gastos registrados este mes.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Card({ label, value, sub, big, accent, danger }: { label: string; value: string; sub?: string; big?: boolean; accent?: boolean; danger?: boolean }) {
  return (
    <div style={{ background: theme.bg, border: `1px solid ${accent ? theme.accent : danger ? theme.err : theme.border}`, padding: '14px 16px' }}>
      <div style={{ fontSize: 10, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>{label}</div>
      <div style={{ fontFamily: theme.font.head, fontSize: big ? 30 : 22, fontWeight: 600, color: danger ? theme.err : theme.brand, marginTop: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: theme.textMuted }}>{sub}</div>}
    </div>
  );
}

// ===================== GASTOS =====================
function Gastos() {
  const [rows, setRows] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase.from('expenses').select('*').eq('active', true).order('is_recurring', { ascending: false }).order('expense_date', { ascending: false }).limit(500);
    setRows((data as Expense[]) ?? []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  async function saveAmount(id: string, amount: number) {
    await supabase.from('expenses').update({ amount, updated_at: new Date().toISOString() }).eq('id', id);
    load();
  }
  async function remove(id: string) {
    if (!confirm('¿Eliminar este gasto?')) return;
    await supabase.from('expenses').update({ active: false }).eq('id', id);
    load();
  }

  const recurring = rows.filter(r => r.is_recurring);
  const oneTime = rows.filter(r => !r.is_recurring);
  const mx = (n: number, c: string) => `$${Number(n).toLocaleString('es-MX', { maximumFractionDigits: 0 })} ${c}`;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ fontSize: 12, color: theme.textMuted }}>{loading ? 'Cargando…' : `${recurring.length} recurrentes · ${oneTime.length} gastos únicos`}</div>
        <button onClick={() => setShowAdd(true)} style={btn}>+ Nuevo gasto</button>
      </div>

      {showAdd && <AddExpense onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}

      <h3 style={h3}>Recurrentes (cada mes)</h3>
      <div style={{ background: theme.bg, border: `1px solid ${theme.border}`, marginBottom: 20, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr style={{ background: theme.bgSoft }}><Th>Categoría</Th><Th>Descripción</Th><Th style={{ textAlign: 'right' }}>Monto mensual</Th><Th style={{ width: 60 }}>{''}</Th></tr></thead>
          <tbody>
            {recurring.map(r => (
              <tr key={r.id} style={{ borderBottom: `1px solid ${theme.border}` }}>
                <Td><Badge>{CAT_LABEL[r.category] ?? r.category}</Badge></Td>
                <Td>{r.description}</Td>
                <Td style={{ textAlign: 'right' }}>
                  <input type="number" defaultValue={r.amount} onBlur={e => { const v = Number(e.target.value); if (v !== r.amount) saveAmount(r.id, v); }}
                    style={{ width: 110, padding: '4px 6px', border: `1px solid ${theme.border}`, background: theme.bg, color: theme.text, fontSize: 13, textAlign: 'right' }} /> <span style={{ fontSize: 11, color: theme.textMuted }}>{r.currency}</span>
                </Td>
                <Td><button onClick={() => remove(r.id)} style={btnX}>×</button></Td>
              </tr>
            ))}
            {recurring.length === 0 && <tr><td style={{ padding: 14, color: theme.textMuted, fontSize: 12 }}>Sin gastos recurrentes.</td></tr>}
          </tbody>
        </table>
      </div>

      <h3 style={h3}>Gastos únicos</h3>
      <div style={{ background: theme.bg, border: `1px solid ${theme.border}`, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr style={{ background: theme.bgSoft }}><Th>Fecha</Th><Th>Categoría</Th><Th>Descripción</Th><Th>Proveedor</Th><Th style={{ textAlign: 'right' }}>Monto</Th><Th style={{ width: 60 }}>{''}</Th></tr></thead>
          <tbody>
            {oneTime.map(r => (
              <tr key={r.id} style={{ borderBottom: `1px solid ${theme.border}` }}>
                <Td style={{ color: theme.textMuted }}>{new Date(r.expense_date + 'T00:00:00').toLocaleDateString('es-MX')}</Td>
                <Td><Badge>{CAT_LABEL[r.category] ?? r.category}</Badge></Td>
                <Td>{r.description}</Td>
                <Td style={{ color: theme.textMuted }}>{r.payee ?? '—'}</Td>
                <Td style={{ textAlign: 'right', fontWeight: 600 }}>{mx(r.amount, r.currency)}</Td>
                <Td><button onClick={() => remove(r.id)} style={btnX}>×</button></Td>
              </tr>
            ))}
            {oneTime.length === 0 && <tr><td style={{ padding: 14, color: theme.textMuted, fontSize: 12 }}>Sin gastos únicos registrados.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AddExpense({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({ expense_date: new Date().toISOString().slice(0, 10), category: 'otros', description: '', payee: '', amount: '', currency: 'MXN', is_recurring: false });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!f.amount) { setErr('Captura el monto'); return; }
    setSaving(true); setErr(null);
    const { error } = await supabase.from('expenses').insert({
      expense_date: f.expense_date, category: f.category, description: f.description || null,
      payee: f.payee || null, amount: Number(f.amount), currency: f.currency,
      is_recurring: f.is_recurring, recurrence: f.is_recurring ? 'monthly' : null,
    });
    setSaving(false);
    if (error) { setErr(error.message); return; }
    onSaved();
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: theme.bg, border: `1px solid ${theme.border}`, padding: 28, width: '100%', maxWidth: 520, position: 'relative' }}>
        <button type="button" onClick={onClose} aria-label="Cerrar" title="Cerrar" style={{ position: 'absolute', top: 10, right: 14, background: 'transparent', border: 'none', color: theme.textMuted, fontSize: 26, lineHeight: 1, cursor: 'pointer', padding: 4, zIndex: 5 }}>×</button>
        <h2 style={{ fontFamily: theme.font.head, fontSize: 20, fontWeight: 400, color: theme.brand, margin: '0 0 16px' }}>Nuevo gasto</h2>
        <form onSubmit={save}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Fecha"><input type="date" value={f.expense_date} onChange={e => setF({ ...f, expense_date: e.target.value })} style={inp} /></Field>
            <Field label="Categoría"><select value={f.category} onChange={e => setF({ ...f, category: e.target.value })} style={inp}>{CATS.map(c => <option key={c} value={c}>{CAT_LABEL[c]}</option>)}</select></Field>
          </div>
          <Field label="Descripción"><input value={f.description} onChange={e => setF({ ...f, description: e.target.value })} style={inp} /></Field>
          <Field label="Proveedor / beneficiario"><input value={f.payee} onChange={e => setF({ ...f, payee: e.target.value })} style={inp} /></Field>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
            <Field label="Monto"><input type="number" step="0.01" value={f.amount} onChange={e => setF({ ...f, amount: e.target.value })} style={inp} /></Field>
            <Field label="Moneda"><select value={f.currency} onChange={e => setF({ ...f, currency: e.target.value })} style={inp}><option value="MXN">MXN</option><option value="USD">USD</option></select></Field>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: theme.brand, cursor: 'pointer', margin: '4px 0 8px' }}>
            <input type="checkbox" checked={f.is_recurring} onChange={e => setF({ ...f, is_recurring: e.target.checked })} /> Es recurrente (cada mes)
          </label>
          {err && <div style={{ color: theme.err, fontSize: 12, marginBottom: 8 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
            <button type="button" onClick={onClose} style={btnGhost}>Cancelar</button>
            <button type="submit" disabled={saving} style={{ ...btn, opacity: saving ? 0.6 : 1 }}>{saving ? 'Guardando…' : 'Guardar gasto'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label style={{ display: 'block', marginBottom: 12 }}><div style={{ fontSize: 11, color: theme.textMuted, marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>{label}</div>{children}</label>;
}
function SubBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return <button onClick={onClick} style={{ background: active ? theme.brand : 'transparent', color: active ? theme.textOnBrand : theme.brand, border: 'none', padding: '10px 16px', fontSize: 12, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', cursor: 'pointer', fontFamily: theme.font.sans }}>{children}</button>;
}
function Th({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: theme.textMuted, borderBottom: `1px solid ${theme.border}`, ...style }}>{children}</th>;
}
function Td({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <td style={{ padding: '8px 12px', verticalAlign: 'middle', ...style }}>{children}</td>;
}
function Badge({ children }: { children: React.ReactNode }) {
  return <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', background: theme.bgFooter, color: theme.brand, textTransform: 'uppercase', letterSpacing: 0.4 }}>{children}</span>;
}

const h3: React.CSSProperties = { fontFamily: theme.font.head, fontSize: 13, fontWeight: 700, color: theme.brand, textTransform: 'uppercase', letterSpacing: 0.6, margin: '0 0 10px' };
const inp: React.CSSProperties = { width: '100%', background: theme.bg, border: `1px solid ${theme.border}`, padding: '9px 11px', color: theme.text, fontSize: 13, fontFamily: theme.font.sans, outline: 'none', boxSizing: 'border-box' };
const btn: React.CSSProperties = { background: theme.accent, color: theme.brand, border: 'none', padding: '9px 16px', fontSize: 12, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', cursor: 'pointer', fontFamily: theme.font.sans };
const btnGhost: React.CSSProperties = { background: 'transparent', border: `1px solid ${theme.border}`, color: theme.brand, padding: '9px 16px', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', cursor: 'pointer', fontFamily: theme.font.sans };
const btnX: React.CSSProperties = { background: 'transparent', border: 'none', color: theme.textMuted, cursor: 'pointer', fontSize: 18, lineHeight: 1 };
